import React, { useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown } from 'lucide-react';

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

type FaqSection = {
  id: string;
  title: string;
  body: React.ReactNode;
};

const sections: FaqSection[] = [
  {
    id: 'what-is-refract',
    title: '1) What is Refract?',
    body: (
      <p>
        Refract is a media discovery app that shuffles and searches your indexed photos/videos across Google Drive, Dropbox, and local files.
        It focuses on fast playback plus metadata-based retrieval instead of migrating your full media library to Refract servers.
      </p>
    ),
  },
  {
    id: 'indexed-items',
    title: '2) What are indexed items?',
    body: (
      <p>
        Indexed items are media records in your library catalog. Each record stores lightweight metadata such as file ID, name, mime type,
        source, and optional thumbnail references. Indexed count is what plans limit (for Drive/Dropbox items).
      </p>
    ),
  },
  {
    id: 'sources',
    title: '3) Which sources are supported?',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Google Drive files through OAuth + picker/folder import.</li>
        <li>Dropbox files through OAuth + import/sync endpoints.</li>
        <li>Local-only files stored in IndexedDB on your device.</li>
      </ul>
    ),
  },
  {
    id: 'persistence',
    title: '4) What persists across sessions?',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Local persistence: auth session cache, metadata, local file blobs, image cache, usage/search caches (IndexedDB/localStorage).</li>
        <li>Server persistence: account plan/usage, remote library records, Dropbox connection/session, subscription state.</li>
        <li>Sign-out behavior: local-only file bytes/metadata are cleared unless you enabled cloud backup.</li>
      </ul>
    ),
  },
  {
    id: 'cloud-backup',
    title: '5) How does cloud backup work?',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Cloud backup is a Pro/Power feature.</li>
        <li>When enabled, local files are uploaded via `/api/backup/local` for restore on later sessions/devices.</li>
        <li>Backups include file content plus metadata like hash/name/mime type/size.</li>
      </ul>
    ),
  },
  {
    id: 'privacy-security',
    title: '6) Privacy and security',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Google Drive and Dropbox access uses user-authorized OAuth tokens.</li>
        <li>Media playback typically streams from source links; local items remain device-local unless cloud backup is enabled.</li>
        <li>Third parties involved include Google Drive, Dropbox, and Stripe for billing.</li>
      </ul>
    ),
  },
  {
    id: 'pricing',
    title: '7) Pricing tiers',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Free: up to 2,000 indexed Drive/Dropbox items, no AI tags, no collections, no cloud backup.</li>
        <li>Pro: up to 50,000 indexed items, AI search, collections (up to 50), cloud backup.</li>
        <li>Power: up to 200,000 indexed items, AI search, collections (up to 500), cloud backup + premium features.</li>
      </ul>
    ),
  },
  {
    id: 'collections',
    title: '8) Collections',
    body: (
      <p>
        Collections are named subsets of indexed items. You can create, rename, delete, and scope shuffle/search to a collection.
        Availability and limits depend on your plan.
      </p>
    ),
  },
  {
    id: 'player-controls',
    title: '9) Player controls',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>Shuffle queue, previous/next, interval timing, and media type filter (all/photos/videos).</li>
        <li>Fullscreen/pseudo-fullscreen and quick source import actions.</li>
        <li>AI analysis/search is gated by plan capabilities.</li>
      </ul>
    ),
  },
  {
    id: 'troubleshooting',
    title: '10) Troubleshooting',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Items disappear:</strong> check collection scope/filter mode and whether you signed out (local-only is cleared).</li>
        <li><strong>Dropbox not connecting:</strong> verify Dropbox app key/env + reconnect from Settings.</li>
        <li><strong>Upgrade not reflected:</strong> open Plans and refresh billing state after checkout.</li>
        <li><strong>Usage mismatch:</strong> the app now uses one computed indexed count source for bar + plans modal.</li>
      </ul>
    ),
  },
  {
    id: 'developer-api',
    title: '11) Developer API',
    body: (
      <p>
        Developer API details live at <a className="text-indigo-300/90 hover:text-indigo-200" href="/developers">/developers</a>.
        It is intended for technical integrations and is plan-gated (viewable page with upgrade CTA).
      </p>
    ),
  },
];

const FaqPage: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toc = useMemo(() => sections.map((section) => ({ id: section.id, title: section.title })), []);

  return (
    <div className="min-h-screen bg-[#0C0A14] text-white">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-10">
        <a href="/" className="inline-flex items-center gap-2 text-white/45 hover:text-white/80 text-sm mb-6" style={{ fontFamily: SANS }}>
          <ArrowLeft size={14} />
          Back to Refract
        </a>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden">
          <div className="px-6 md:px-8 py-6 border-b border-white/10">
            <h1 className="text-4xl md:text-5xl font-light" style={{ fontFamily: SERIF }}>FAQ</h1>
            <p className="text-white/55 text-sm mt-2 max-w-3xl" style={{ fontFamily: SANS }}>
              This page reflects the current codebase behavior for indexing, persistence, plans, backup, and developer access.
            </p>
          </div>

          <div className="md:hidden px-6 py-4 border-b border-white/10">
            <button onClick={() => setMobileOpen((prev) => !prev)} className="w-full flex items-center justify-between rounded-xl border border-white/15 px-3 py-2 text-sm text-white/75" style={{ fontFamily: SANS }}>
              Table of contents
              <ChevronDown size={14} className={`transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileOpen && (
              <div className="mt-2 space-y-1">
                {toc.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="block rounded-lg px-3 py-2 text-sm text-white/65 hover:text-white hover:bg-white/5" style={{ fontFamily: SANS }}>
                    {item.title}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="hidden md:block border-r border-white/10 p-5 sticky top-0 h-fit">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/30 mb-3" style={{ fontFamily: SANS }}>Contents</p>
              <div className="space-y-1">
                {toc.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="block rounded-lg px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/5" style={{ fontFamily: SANS }}>
                    {item.title}
                  </a>
                ))}
              </div>
            </aside>

            <main className="p-6 md:p-8 space-y-8">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-2xl font-light mb-3" style={{ fontFamily: SERIF }}>{section.title}</h2>
                  <div className="text-white/70 text-sm leading-7" style={{ fontFamily: SANS }}>
                    {section.body}
                  </div>
                </section>
              ))}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
